import { PatientSuggestionError, RelationType } from '@/api'
import { ReactComponent as ArrowLeft } from '@/assets/arrow-left.svg'
import { ReactComponent as InfoIcon } from '@/assets/info.svg'
import {
  Banner,
  DateInput,
  PageLoader,
  PrimaryButton,
  Select,
  selectOptionType,
} from '@/components'
import { FormItem, FormProvider, useAuthContext, useFormContext, useSnackbar } from '@/providers'
import { paths } from '@/routes/paths'
import {
  useCreatePatientSuggestion,
  useSubmitOnboarding,
  useSuggestableStationList,
} from '@/services'
import { validateDate } from '@/utils'
import { FormikConfig } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Location, useLocation, useNavigate } from 'react-router-dom'
import { number, object, string } from 'yup'

export type SuggestPatientState = {
  hospital?: string
  station?: number
  date_of_birth?: string
  patient_first_name?: string
  patient_last_name?: string
}

type SuggestFormValues = {
  station: number | ''
  first_name: string
  last_name: string
  date_of_birth: string
  relation: RelationType | ''
}

const useMessages = () => {
  const { t } = useTranslation()

  return {
    title: t('suggestPatientPage.title'),
    text: t('suggestPatientPage.text'),
    hintTitle: t('suggestPatientPage.hintTitle'),
    hintText: t('suggestPatientPage.hintText'),
    ward: t('global.ward'),
    birthdate: t('global.birthdate'),
    firstNameLabel: t('suggestPatientPage.firstNameLabel'),
    lastNameLabel: t('suggestPatientPage.lastNameLabel'),
    relationLabel: t('onboardingPage.step4Label'),
    relationPlaceholder: t('onboardingPage.step4Placeholder'),
    button: t('suggestPatientPage.button'),
    noStations: t('suggestPatientPage.noStations'),
    errors: t('suggestPatientPage.errors', { returnObjects: true }),
    success: t('suggestPatientPage.success', { returnObjects: true }),
    validations: t('suggestPatientPage.validations', { returnObjects: true }),
    generalError: t('global.messageError'),
  }
}

const validationSchema = (validations: {
  station: string
  firstName: string
  lastName: string
  birthdate: string
  relation: string
}) =>
  object({
    station: number().required(validations.station),
    first_name: string().trim().required(validations.firstName),
    last_name: string().trim().required(validations.lastName),
    date_of_birth: string().test('date-check', validations.birthdate, validateDate),
    relation: string().required(validations.relation),
  })

const SuggestForm = ({ stations }: { stations: selectOptionType[] }) => {
  const { isSubmitting, isValid } = useFormContext<SuggestFormValues>()
  const { settings } = useAuthContext()
  const messages = useMessages()

  return (
    <div className="flex flex-col gap-4 h-full w-full">
      <FormItem<typeof Select>
        component={Select}
        name="station"
        label={`${messages.ward}*`}
        componentProps={{ readOnly: true, options: stations }}
      />
      <FormItem
        name="first_name"
        label={`${messages.firstNameLabel}*`}
        componentProps={{ inputType: 'name' }}
      />
      <FormItem
        name="last_name"
        label={`${messages.lastNameLabel}*`}
        componentProps={{ inputType: 'name' }}
      />
      <FormItem<typeof DateInput>
        component={DateInput}
        name="date_of_birth"
        label={`${messages.birthdate}*`}
        animateLabel={false}
      />
      <div className="flex flex-col gap-2 w-full pt-2">
        <span className="text-h4">{messages.relationLabel}:</span>
        <FormItem<typeof Select>
          component={Select}
          name="relation"
          componentProps={{
            readOnly: true,
            placeholder: `${messages.relationPlaceholder}*`,
            options: settings.relationships,
          }}
        />
      </div>
      <div className="flex-1" />
      <PrimaryButton type="submit" block disabled={isSubmitting || !isValid}>
        {messages.button}
      </PrimaryButton>
    </div>
  )
}

/**
 * Patientenvorschlag: letzter Ausweg, wenn die Zuordnungssuche niemanden findet, weil die
 * Station den/die Patient:in noch nicht angelegt hat. Die Angaben gehen als Vorschlag an die
 * Station - erst wenn sie ihn uebernimmt, entsteht ein Patientendatensatz und die Zuordnung.
 */
export const SuggestPatientPage = () => {
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const messages = useMessages()
  const [isSent, setIsSent] = useState(false)
  const { state } = useLocation() as Omit<Location, 'state'> & { state?: SuggestPatientState }

  const createSuggestionMutation = useCreatePatientSuggestion()
  const submitOnboardingMutation = useSubmitOnboarding()
  const { data: stations = [], isLoading } = useSuggestableStationList(state?.hospital || '')
  const stationOptions: selectOptionType[] = stations.map((station) => ({
    value: station.id,
    label: station.name,
  }))

  const goHome = () => navigate(`/${paths.home}`)

  const handleSubmit: FormikConfig<SuggestFormValues>['onSubmit'] = (values, { setSubmitting }) => {
    createSuggestionMutation.mutate(
      {
        station: values.station as number,
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        date_of_birth: values.date_of_birth,
        relation: values.relation as RelationType,
      },
      {
        onSuccess: () => setIsSent(true),
        onError: (err) => {
          setSubmitting(false)
          const data = err.response?.data as PatientSuggestionError | undefined

          // Die Station war schneller: dann ist es keine Neuanlage, sondern eine
          // gewoehnliche Zuordnungsanfrage - die schicken wir direkt hinterher.
          if (data?.patient_found && data.patient) {
            submitOnboardingMutation.mutate(
              {
                patient_id: data.patient.id,
                patient_relation: values.relation as RelationType,
              },
              {
                onSuccess: () => {
                  snackbar.success(messages.errors.patientFound)
                  navigate(`/${paths.home}/${data.patient?.id}`)
                },
                onError: () => snackbar.error(messages.generalError),
              },
            )
            return
          }

          if (data?.already_suggested) return snackbar.error(messages.errors.alreadySuggested)
          if (data?.too_many_pending) return snackbar.error(messages.errors.tooManyPending)
          if (data?.suggestions_disabled) return snackbar.error(messages.errors.disabled)

          snackbar.error(messages.generalError)
        },
      },
    )
  }

  if (isLoading) return <PageLoader />

  return (
    <div className="page flex justify-center pt-[60px] bg-sec0">
      <div className="subpage-header">
        <ArrowLeft
          height={24}
          width={24}
          fill="var(--primary)"
          className="cursor-pointer"
          onClick={goHome}
        />
        <div className="text-textM">{messages.title}</div>
        <div />
      </div>
      <div className="max-w-[488px] w-full flex flex-col gap-6 items-center p-6">
        {isSent ? (
          <>
            <span className="text-h2reg text-center">{messages.success.title}</span>
            <Banner
              title={messages.success.bannerTitle}
              icon={<InfoIcon fill="var(--primary)" height={24} width={24} className="shrink-0" />}
            >
              {messages.success.bannerText}
            </Banner>
            <span className="text-b90 text-textS">{messages.success.text}</span>
            <PrimaryButton block onClick={goHome}>
              {messages.success.button}
            </PrimaryButton>
          </>
        ) : stationOptions.length ? (
          <>
            <div className="text-textS text-b90">{messages.text}</div>
            <Banner
              title={messages.hintTitle}
              icon={<InfoIcon fill="var(--primary)" height={24} width={24} className="shrink-0" />}
            >
              {messages.hintText}
            </Banner>
            <FormProvider<SuggestFormValues>
              initialValues={{
                station: state?.station ?? '',
                first_name: state?.patient_first_name || '',
                last_name: state?.patient_last_name || '',
                date_of_birth: state?.date_of_birth || '',
                relation: '',
              }}
              onSubmit={handleSubmit}
              validationSchema={validationSchema(messages.validations)}
              className="w-full flex flex-col gap-8 h-full"
            >
              <SuggestForm stations={stationOptions} />
            </FormProvider>
          </>
        ) : (
          <>
            <div className="text-textS text-b90">{messages.noStations}</div>
            <PrimaryButton block onClick={goHome}>
              {messages.success.button}
            </PrimaryButton>
          </>
        )}
      </div>
    </div>
  )
}
